import { Box, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, Typography } from '@mui/material';
import moment from 'moment';
import { ReactNode } from 'react';
import {
  AutocompleteArrayInput,
  AutocompleteInput,
  ChipField,
  Datagrid,
  DateField,
  FunctionField,
  ReferenceArrayField,
  ReferenceArrayInput,
  ReferenceField,
  ReferenceInput,
  SelectArrayInput,
  SingleFieldList,
  TextField,
  useCreatePath,
  useListContext,
  useTranslate,
} from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts';
import { Permission, ReportList, RotatedAxisTick } from '../../components';
import { BetweenDatesInput } from '../../components/BetweenDatesInput';
import ListPagination from '../../components/ListPagination';
import { PERMISSIONS } from '../../constants';
import { formatReal, getChainId } from '../../lib';
import { filterToQuery } from '../../utils';
import { __t } from '../../i18n/t';

/**
 * Relatório "Divergência de preço com desconto" (#4106).
 *
 * O backend classifica cada abastecimento com desconto cujo preço gravado ficou igual ao
 * preço cheio (o mesmo caso do alerta "Preço não alterado com desconto usado") comparando
 * o preço gravado com o que a bomba cobrou de verdade — `valor / litros`.
 */
const PATTERNS = {
  truncated_cent: 'Concentrador truncou 1 centavo',
  full_price: 'Concentrador mandou preço cheio',
  discount_not_applied: 'Desconto não aplicado na bomba',
  undetermined: 'Não classificado',
};

/** Nos dois primeiros o desconto saiu na bomba e só o nosso registro está errado. */
const RECORD_ONLY_PATTERNS = ['truncated_cent', 'full_price'];

const PATTERN_MEANING = {
  truncated_cent: 'Desconto saiu na bomba, registro errado',
  full_price: 'Desconto saiu na bomba, registro errado',
  discount_not_applied: 'Cliente pagou o preço cheio',
  undetermined: 'Nenhuma hipótese fecha com o total',
};

const chartColor = '#608DE0';
const lostValueColor = '#E0348A';

const borderColor = '#E0E0E0';

/** O provider (getAll) lê perPage nulo como "sem paginação" e traz a lista inteira. */
const ALL_ITEMS = null as unknown as number;

const formatPercent = (value: number) => `${Number(value || 0).toFixed(2).replace('.', ',')}%`;
const formatPrice = (value: number | null) => value === null || value === undefined ? '—' : `R$ ${Number(value).toFixed(3).replace('.', ',')}`;
const formatAmount = (value: number) => Number(value || 0).toFixed(3).replace('.', ',');
const formatSignedReal = (value: number) => `${Number(value || 0) < 0 ? '- ' : ''}${formatReal(Math.abs(Number(value || 0)))}`;
const formatStation = (record: { stationName?: string, numericIds?: number[] }) => {
  const numericId = record.numericIds?.length ? ` (${record.numericIds.join(', ')})` : '';
  return `${record.stationName || __t('Posto não identificado')}${numericId}`;
};

/** Bloco fechado: cada seção do relatório tem moldura, cabeçalho e uma linha do que ela responde. */
const Section = ({ title, hint, children }: { title: string, hint?: string, children: ReactNode }) => (
  <Card
    variant="outlined"
    sx={{ marginBottom: 3, borderColor, borderRadius: 2, overflow: 'hidden', breakInside: 'avoid' }}
  >
    <Box sx={{ padding: '10px 16px', backgroundColor: '#FAFAFA', borderBottom: `1px solid ${borderColor}` }}>
      <Typography sx={{ fontWeight: 600, fontSize: 15 }}>{title}</Typography>
      {hint && <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{hint}</Typography>}
    </Box>
    {children}
  </Card>
);

const EmptyState = ({ children }: { children: ReactNode }) => (
  <Typography sx={{ padding: 3, textAlign: 'center', color: 'text.secondary', fontSize: 13 }}>
    {children}
  </Typography>
);

const StatTile = ({ label, value, hint }: { label: string, value: string, hint?: string }) => (
  <Box sx={{ flex: '1 1 180px', minWidth: 170, padding: '12px 16px', border: `1px solid ${borderColor}`, borderRadius: 2 }}>
    <Typography sx={{ fontSize: 11, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</Typography>
    <Typography sx={{ fontSize: 22, fontWeight: 600, lineHeight: 1.4 }}>{value}</Typography>
    {hint && <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{hint}</Typography>}
  </Box>
);

type Column = {
  label: string,
  render: (record: any) => ReactNode,
  align?: 'left' | 'right',
  /** Campo aceito pelo ORDER BY do endpoint; sem ele a coluna não é clicável. */
  sortField?: string,
};

/**
 * Tabela das seções: cabeçalho destacado, linhas zebradas e número alinhado à direita.
 * O GenericDatagrid não serve aqui — ele divide a largura em partes iguais e não delimita
 * o corpo da tabela, que é justamente o que embaralha a leitura de seções empilhadas.
 */
const DataTable = ({ columns, rows, emptyText, rowResource, sort, setSort }: {
  columns: Column[],
  rows: any[],
  emptyText: string,
  rowResource?: string,
  sort?: { field: string, order: string },
  setSort?: (sort: { field: string, order: 'ASC' | 'DESC' }) => void,
}) => {
  const navigate = useNavigate();
  const createPath = useCreatePath();

  if (!rows.length) { return <EmptyState>{emptyText}</EmptyState>; }

  const openRecord = (record: any) => () => {
    if (!rowResource || !record.id) { return; }
    navigate(createPath({ resource: rowResource, id: record.id, type: 'show' }));
  };

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map(column => (
              <TableCell
                key={column.label}
                align={column.align || 'left'}
                sx={{
                  backgroundColor: '#F5F5F5',
                  borderBottom: `1px solid ${borderColor}`,
                  fontWeight: 600,
                  fontSize: 12,
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                  letterSpacing: 0.3,
                  whiteSpace: 'nowrap',
                }}
              >
                {column.sortField && sort && setSort
                  ? <TableSortLabel
                      active={sort.field === column.sortField}
                      direction={sort.field === column.sortField ? sort.order.toLowerCase() as 'asc' | 'desc' : 'desc'}
                      onClick={() => setSort({
                        field: column.sortField as string,
                        order: sort.field === column.sortField && sort.order === 'DESC' ? 'ASC' : 'DESC',
                      })}
                    >
                      {column.label}
                    </TableSortLabel>
                  : column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((record, index) => (
            <TableRow
              key={record.id || index}
              onClick={openRecord(record)}
              sx={{
                cursor: rowResource ? 'pointer' : 'default',
                backgroundColor: index % 2 ? '#FAFAFA' : 'inherit',
                '&:hover': { backgroundColor: rowResource ? '#EEF4FF' : undefined },
              }}
            >
              {columns.map(column => (
                <TableCell
                  key={column.label}
                  align={column.align || 'left'}
                  sx={{ borderBottom: '1px solid #F0F0F0', fontSize: 13, whiteSpace: 'nowrap' }}
                >
                  {column.render(record)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const Summary = ({ totals }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, padding: 2 }}>
    <StatTile
      label="Com divergência"
      value={String(totals.divergedFillins || 0)}
      hint={`${formatPercent(totals.divergenceRate)} dos ${totals.discountFillins || 0} com desconto`}
    />
    <StatTile
      label="Desconto não registrado"
      value={formatReal(totals.unrecordedDiscountValue)}
      hint="Saiu na bomba, faltou no nosso registro"
    />
    <StatTile
      label="Desconto não entregue"
      value={formatReal(totals.notAppliedDiscountValue)}
      hint="Cliente pagou o preço cheio"
    />
    <StatTile
      label="A apurar"
      value={formatReal(totals.undeterminedValue)}
      hint={`${totals.withoutRuleFillins || 0} sem regra de desconto vinculada`}
    />
    <StatTile
      label="Erro no valor"
      value={formatReal(totals.recordValueGap)}
      hint="Soma em módulo do erro do preço gravado"
    />
    <StatTile
      label="Atingidos"
      value={`${totals.customers || 0} clientes`}
      hint={`${totals.stations || 0} postos · ${totals.chains || 0} redes`}
    />
    <StatTile
      label="Volume envolvido"
      value={formatReal(totals.divergedValue)}
      hint={`${formatAmount(totals.divergedAmount)} litros`}
    />
    <StatTile
      label="Alertas disparados"
      value={String(totals.alerts || 0)}
      hint="Gravados pelo socket; não casam 1 a 1 com a lista"
    />
  </Box>
);

const patternColumns = (divergedFillins: number): Column[] => [
  { label: 'Padrão da divergência', render: record => __t(PATTERNS[record.id] || record.id) },
  { label: 'O que aconteceu', render: record => (PATTERN_MEANING[record.id] ? __t(PATTERN_MEANING[record.id]) : '') },
  { label: 'Abastecimentos', align: 'right', render: record => String(record.fillins) },
  { label: 'Participação', align: 'right', render: record => formatPercent(divergedFillins ? (record.fillins / divergedFillins) * 100 : 0) },
  { label: 'Desconto perdido', align: 'right', render: record => formatReal(record.value) },
];

const stationColumns: Column[] = [
  { label: 'Posto', render: formatStation },
  { label: 'Rede', render: record => record.chainName },
  { label: 'Com desconto', align: 'right', render: record => String(record.discountFillins) },
  { label: 'Com divergência', align: 'right', render: record => String(record.divergedFillins) },
  { label: 'Incidência', align: 'right', render: record => formatPercent(record.divergenceRate) },
  { label: 'Registro errado', align: 'right', render: record => String(record.concentratorFillins) },
  { label: 'Não aplicado', align: 'right', render: record => String(record.notAppliedFillins) },
  { label: 'Erro no valor', align: 'right', render: record => formatReal(record.recordValueGap) },
  { label: 'Desconto perdido', align: 'right', render: record => formatReal(record.lostDiscountValue) },
  { label: 'Última', align: 'right', render: record => moment(record.lastDivergenceAt).format('DD/MM/YYYY HH:mm') },
];

/** No padrão "não classificado" o valor é teto a apurar, não prejuízo confirmado. */
const LostDiscount = ({ record }) => (
  <Box component="span" sx={{ color: record.pattern === 'undetermined' ? 'text.secondary' : 'inherit' }}>
    {formatReal(record.lostDiscountValue)}
    {record.pattern === 'undetermined' ? ' *' : ''}
  </Box>
);

const fillinColumns: Column[] = [
  { label: 'Data', sortField: 'receivedAt', render: record => moment(record.receivedAt).format('DD/MM/YYYY HH:mm') },
  { label: 'Posto', sortField: 'stationName', render: formatStation },
  { label: 'Rede', sortField: 'chainName', render: record => record.chainName },
  { label: 'Bico', sortField: 'hoseNumber', align: 'right', render: record => String(record.hoseNumber ?? '—') },
  { label: 'Combustível', sortField: 'fuelName', render: record => record.fuelName },
  { label: 'Cliente', sortField: 'customerName', render: record => record.customerName || __t('Não identificado') },
  { label: 'Desconto', sortField: 'discountName', render: record => record.discountName || '—' },
  { label: 'Litros', sortField: 'amount', align: 'right', render: record => formatAmount(record.amount) },
  { label: 'Valor', sortField: 'value', align: 'right', render: record => formatReal(record.value) },
  { label: 'Preço gravado', sortField: 'recordedPrice', align: 'right', render: record => formatPrice(record.recordedPrice) },
  { label: 'Preço cobrado', sortField: 'chargedPrice', align: 'right', render: record => formatPrice(record.chargedPrice) },
  { label: 'Erro no valor', sortField: 'recordValueGap', align: 'right', render: record => formatSignedReal(record.recordValueGap) },
  { label: 'Desconto cadastrado/L', sortField: 'expectedDiscountPerLiter', align: 'right', render: record => formatPrice(record.expectedDiscountPerLiter) },
  { label: 'Desconto perdido', sortField: 'lostDiscountValue', align: 'right', render: record => <LostDiscount record={record} /> },
  { label: 'Padrão da divergência', sortField: 'pattern', render: record => __t(PATTERNS[record.pattern] || record.pattern) },
];

/**
 * Dois gráficos de um eixo só: ocorrências e reais têm escalas diferentes e não dividem
 * o mesmo eixo sem mentir sobre a proporção entre as duas séries.
 */
const DailyCharts = ({ byDay }) => {
  if (!byDay.length) {
    return <EmptyState>Sem divergência no período — nada para plotar.</EmptyState>;
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center', padding: 2 }}>
      <Box>
        <Typography sx={{ textAlign: 'center', fontSize: 13, color: 'text.secondary' }}>Abastecimentos</Typography>
        <BarChart width={520} height={300} data={byDay}>
          <CartesianGrid strokeDasharray="3 3" stroke="#EEEEEE" vertical={false} />
          <XAxis dataKey="date" tick={<RotatedAxisTick fontSize={12} />} height={70} />
          <YAxis allowDecimals={false} />
          <Tooltip />
          <Bar dataKey="divergedFillins" fill={chartColor} name={__t('Abastecimentos')} radius={[4, 4, 0, 0]} />
        </BarChart>
      </Box>
      <Box>
        <Typography sx={{ textAlign: 'center', fontSize: 13, color: 'text.secondary' }}>Desconto perdido em reais</Typography>
        <BarChart width={520} height={300} data={byDay}>
          <CartesianGrid strokeDasharray="3 3" stroke="#EEEEEE" vertical={false} />
          <XAxis dataKey="date" tick={<RotatedAxisTick fontSize={12} />} height={70} />
          <YAxis />
          <Tooltip formatter={value => formatReal(Number(value))} />
          <Bar dataKey="lostDiscountValue" fill={lostValueColor} name={__t('Desconto perdido')} radius={[4, 4, 0, 0]} />
        </BarChart>
      </Box>
    </Box>
  );
};

const PriceDivergence = () => {
  const { data, sort, setSort } = useListContext();
  const report = data?.[0];
  const summary = report?.summaryData || {};
  const totals = summary.totals || {};
  const fillins = report?.data || [];

  return (
    <Box sx={{ padding: 2 }}>
      {totals.readFromReplica === false &&
        <Typography sx={{ marginBottom: 2, padding: '8px 12px', borderRadius: 2, fontSize: 13, backgroundColor: '#FFF4E5', color: '#8A4B00' }}>
          Consulta saiu do banco principal — a réplica não está configurada neste ambiente.
        </Typography>
      }

      <Section
        title="Resumo"
        hint="Abastecimento com desconto em que o preço gravado não bate com o que a bomba cobrou."
      >
        <Summary totals={totals} />
      </Section>

      <Section title="Padrões" hint="O que explica cada divergência, comparando o preço gravado com valor ÷ litros.">
        <DataTable
          columns={patternColumns(totals.divergedFillins)}
          rows={summary.byPattern || []}
          emptyText="Nenhuma divergência classificada no período."
        />
      </Section>

      <Section title="Por dia" hint="Duas escalas diferentes, um eixo para cada uma.">
        <DailyCharts byDay={summary.byDay || []} />
      </Section>

      <Section title="Postos" hint="Ordenado por ocorrências. Incidência é sobre os abastecimentos com desconto do próprio posto.">
        <DataTable
          columns={stationColumns}
          rows={summary.byStation || []}
          emptyText="Nenhum posto com divergência no período."
          rowResource="places"
        />
      </Section>

      <Section
        title="Abastecimentos com divergência"
        hint="Erro no valor = o quanto o preço gravado deixa de explicar o total cobrado. Desconto perdido = desconto cadastrado × litros, que ficou fora do registro (padrões de concentrador) ou não chegou ao cliente (desconto não aplicado); com * é teto a apurar, não prejuízo confirmado. Clique no cabeçalho para ordenar e na linha para abrir o abastecimento."
      >
        <DataTable
          columns={fillinColumns}
          rows={fillins}
          emptyText="Nenhum abastecimento com divergência no período."
          rowResource="fillins"
          sort={sort}
          setSort={setSort}
        />
      </Section>
    </Box>
  );
};

const priceDivergenceExporter = async (data) => {
  const fillins = data?.[0]?.data || [];

  return fillins.map(fillin => ({
    data: moment(fillin.receivedAt).format('DD/MM/YYYY HH:mm'),
    posto: fillin.stationName,
    numId: fillin.numericIds?.join(' / ') || '',
    rede: fillin.chainName,
    bico: fillin.hoseNumber,
    combustivel: fillin.fuelName,
    cliente: fillin.customerName || 'Não identificado',
    desconto: fillin.discountName || '',
    litros: fillin.amount,
    valor: fillin.value,
    precoGravado: fillin.recordedPrice,
    precoCobrado: fillin.chargedPrice,
    erroNoValor: fillin.recordValueGap,
    descontoCadastradoPorLitro: fillin.expectedDiscountPerLiter,
    descontoPerdido: fillin.lostDiscountValue,
    padrao: PATTERNS[fillin.pattern] || fillin.pattern,
    abastecimento: fillin.id,
    reserva: fillin.reserveId,
  }));
};

const FilterTablePrint = () => {
  const { filterValues } = useListContext();

  return (
    <Datagrid bulkActionButtons={false} data={[{ ...filterValues, generatedReportAt: moment().toISOString() }]} total={1}>
      <DateField source="generatedReportAt" textAlign="center" label="Gerado às" showTime sortable={false} locales="pt-BR" />
      <DateField source="from" textAlign="center" label="De" sortable={false} locales="pt-BR" />
      <DateField source="to" textAlign="center" label="Até" sortable={false} locales="pt-BR" />
      <ReferenceField source="chainId" emptyText="Todas" reference="chains" label="Rede" sortable={false} link={false}>
        <TextField source="name" />
      </ReferenceField>
      <ReferenceArrayField source="stationIds" emptyText="Todos" reference="stations" label="Postos" sortable={false}>
        <SingleFieldList linkType={false}>
          <ChipField source="name" />
        </SingleFieldList>
      </ReferenceArrayField>
      {/* O filtro de padrão muda todos os números da tela; sem ele no impresso, quem lê o papel toma o recorte pelo todo. */}
      <FunctionField
        label="Padrão da divergência"
        textAlign="center"
        sortable={false}
        render={record => record.patterns?.length
          ? record.patterns.map(id => __t(PATTERNS[id] || id)).join(', ')
          : __t('Todos')}
      />
    </Datagrid>
  );
};

const ReportPriceDivergence = (props) => {
  const translate = useTranslate();

  const listFilters = [
    <BetweenDatesInput alwaysOn />,
    <ReferenceInput
      source="chainId"
      reference="chains"
      perPage={ALL_ITEMS}
      sort={{ field: 'name', order: 'ASC' }}
      alwaysOn
    >
      <AutocompleteInput
        filterToQuery={filterToQuery}
        label="Rede"
        style={{ minWidth: 180 }}
        optionText="name"
      />
    </ReferenceInput>,
    <ReferenceArrayInput
      source="stationIds"
      reference="stations"
      perPage={ALL_ITEMS}
      sort={{ field: 'name', order: 'ASC' }}
      alwaysOn
    >
      <AutocompleteArrayInput
        filterToQuery={filterToQuery}
        emptyText="Todos"
        label="Postos"
        sx={{ minWidth: 180, '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': { minWidth: 50 } }}
        optionText="name"
        noOptionsText={translate('ra.input.autocomplete.no_options_text')}
      />
    </ReferenceArrayInput>,
    <SelectArrayInput
      source="patterns"
      label="Padrão da divergência"
      alwaysOn
      sx={{ minWidth: 220 }}
      choices={Object.entries(PATTERNS).map(([id, name]) => ({ id, name }))}
    />,
  ];

  return (
    <Permission permission={PERMISSIONS.ADMIN}>
      <ReportList periodRequired
        {...props}
        basePath="report-price-divergence"
        title="Divergência de Preço"
        resource="reports/price-divergence"
        filters={listFilters}
        sort={{ field: 'receivedAt', order: 'DESC' }}
        // O valor inicial da rede vive aqui: o react-admin recusa `defaultValue` em filtro
        // `alwaysOn` e manda usar filterDefaultValues. Com storeKey={false} abaixo, isso
        // vale em toda abertura da tela, não só na primeira.
        filterDefaultValues={{
          chainId: getChainId(),
          from: moment().startOf('day').subtract(7, 'days').toISOString(),
          to: moment().endOf('day').toISOString(),
        }}
        perPage={25}
        // storeKey={false} desliga o filtro salvo no navegador: a tela sempre abre com 7 dias
        // e a rede do usuário. Sem isso, um "sem rede" salvo numa visita anterior voltaria na
        // próxima e a consulta varreria a fillin inteira.
        storeKey={false as unknown as string}
        pagination={<ListPagination />}
        bulkActionButtons={false}
        customExporter={priceDivergenceExporter}
        titleOnPrint={__t('Relatório de divergência de preço com desconto')}
        filterTablePrint={<FilterTablePrint />}
        landscape
      >
        <PriceDivergence />
      </ReportList>
    </Permission>
  );
};

export default ReportPriceDivergence;
